import { ContainerClient } from "@azure/storage-blob";
import type { BlockBlobClient } from "@azure/storage-blob";
import { t } from "i18next";
import { toast } from "react-toastify";
import FileHashUtils from "./FileHashUtils";
import { UploadManager, UploadRetryStatus } from "./UploadManager";
import {
  endDeploymentUpload,
  endDeploymentUploadWithRetry,
  EndUploadOutcome,
  startDeploymentUpload,
} from "../../api/deploymentsApi";
import { NO_DETAIL_MESSAGE } from "../../api/apiUtils";
import { getFileName } from "../common/helpers";
import { DeploymentStatus } from "../common/ObjectTypes";

/** Files above this size are sent as blocks of this size (`uploadData`'s `blockSize`). */
export const UPLOAD_BLOCK_SIZE_BYTES = 1 * 1024 * 1024;
/** Blocks of one file in flight at once (`uploadData`'s `concurrency`). */
export const UPLOAD_CONCURRENCY = 5;
/**
 * Back-off before the second, third, … attempt at one file (AKL-889). The last entry
 * repeats, so attempts 5 to 8 wait 30 s each. The SDK's own 4 tries at 0/4/12 s run
 * inside every attempt; this schedule sits on top of them.
 */
export const UPLOAD_RETRY_DELAYS_MS: readonly number[] = [
  2000, 5000, 15000, 30000,
];
/** Attempts at one file in one pass before the manager gives it up (AKL-889). */
export const UPLOAD_MAX_ATTEMPTS = 8;
/**
 * The stall budget's floor (AKL-889): a request that reports no progress for this long is
 * aborted and the file retried. See `stallBudgetMs` for when a file gets more.
 */
export const UPLOAD_STALL_TIMEOUT_MS = 60 * 1000;
/**
 * The slowest live link the watchdog must not mistake for a dead one (AKL-889). The SDK
 * reports progress only when a request completes — once per block for a chunked file,
 * once at the end for a file sent in one request — so on a slow link a large file shows
 * no progress for a long time while it is, in fact, moving. Below 20 KB/s a folder of
 * images is not a realistic upload in any case.
 */
export const UPLOAD_STALL_FLOOR_BYTES_PER_SECOND = 20 * 1024;
/**
 * How old a SAS may be before the manager fetches a new one ahead of the next file
 * (AKL-891). The server issues a container SAS valid for 10 h with its start backdated
 * 2 min (`upload_controller.py`, `expiry_hours: int = 10`; the web client never overrides
 * it). 8 h leaves 2 h for the file about to be sent, so a long upload replaces its SAS
 * between files instead of running into a 403 in the middle of one.
 */
export const SAS_REFRESH_AGE_MS = 8 * 60 * 60 * 1000;

/** Overrides for the resilience numbers above — a test seam; production passes none. */
export interface UploadResilienceOptions {
  retryDelaysMs?: readonly number[];
  maxAttempts?: number;
  stallTimeoutMs?: number;
  stallFloorBytesPerSecond?: number;
}

/**
 * How an `uploadData` rejection is treated (AKL-889).
 * - `auth`: the blob store refused the SAS (expired, or any other 403); the SAS path
 *   fetches a new one once, then pauses (AKL-891).
 * - `permanent`: the server refused this file and will keep refusing it; skip the file.
 * - `transient`: retry the file after waiting for the network and backing off.
 * - `exists`: storage refused to write over a blob already under this name (AKL-588); the
 *   file is already in akili, so it is settled as skipped, not retried and not failed.
 */
export type UploadErrorKind = "auth" | "permanent" | "transient" | "exists";

/**
 * Classifies an `uploadData` rejection. `auth`: a storage error whose error code is
 * `AuthenticationFailed` — read from `details.errorCode` (the `x-ms-error-code` header) or
 * from `code` (the `<Code>` of the response body) — whatever its status, and **any**
 * `RestError` with status 403, with or without a readable code. `exists` (AKL-588): any
 * error whose code is `BlobAlreadyExists` (409 in Azure's error-code list), and any
 * `RestError` with status 412. Every upload is sent with `If-None-Match: *` and no other
 * condition, and Microsoft's "Specifying conditional headers for Blob service operations"
 * gives 412 (Precondition Failed) for an unmet `If-None-Match` on a write, so a 412 can only
 * mean the blob is already there. A **bare 409 is not**: storage also answers 409 for
 * `BlockCountExceedsLimit`, `ContainerBeingDeleted` and other conflicts, and skipping such a
 * file would report an upload complete over a photo that was never stored (Copilot, review
 * cycle 1). So a 409 counts only with its code readable, which the SDK also takes from the
 * response body (`code`), not only from the header the browser may hide; otherwise it stays
 * `permanent`, a visible failure kept for Resume. A `RestError` with any other
 * 4xx status except 408 and 429 is `permanent`. Everything else is `transient`: a failed send
 * (`REQUEST_SEND_ERROR`, no status), the watchdog's `AbortError`, any 5xx, 408 and 429, and
 * anything of a shape this function does not recognise — an unknown failure is retried, not
 * fatal.
 *
 * Why every 403 (AKL-891, QA defect of 22 Sep 2026): the SDK fills `details.errorCode` from
 * the `x-ms-error-code` header, which a browser hands over only when the storage account's
 * CORS rule exposes it, so a 403 can arrive with no code at all; it then ran the eight-attempt
 * transient ladder and ended "Upload stopped". Azure Storage documents an expired SAS as
 * `403 AuthenticationFailed` ("Troubleshoot 403 errors in Azure Blob Storage", Microsoft
 * Learn), and every other 403 it documents for a SAS request is also a refusal of the SAS or
 * of the network path — `AuthorizationFailure`, `AuthorizationPermissionMismatch`,
 * `AuthorizationSourceIPMismatch`, `AuthorizationProtocolMismatch`,
 * `AuthorizationResourceTypeMismatch`, `AuthorizationServiceMismatch` ("Shared access
 * signature error codes"), `AccountIsDisabled`, `InsufficientAccountPermissions` ("Common REST
 * API error codes"). Resending the same SAS cannot fix any of them; a fresh SAS fixes the
 * expiry, and for the rest the upload pauses where Resume can pick it up.
 *
 * "A `RestError`" is checked by `name`, not `instanceof`: that is the SDK's own fallback
 * (`isRestError` in `@typespec/ts-http-runtime` is `e instanceof RestError || (isError(e)
 * && e.name === "RestError")`, and the constructor sets `this.name = "RestError"`), and it
 * survives the duplicated-package case `instanceof` does not. Without it any object that
 * happened to carry a numeric 4xx `statusCode` would be permanent, which is the opposite
 * of what the paragraph above promises for an unrecognised failure (review cycle 2).
 * @param error whatever `uploadData` rejected with
 * @returns the kind of failure
 */
export function classifyUploadError(error: unknown): UploadErrorKind {
  if (typeof error !== "object" || error === null) {
    return "transient";
  }
  const { statusCode, details } = error as {
    statusCode?: unknown;
    details?: unknown;
  };
  const errorCode =
    typeof details === "object" && details !== null
      ? (details as { errorCode?: unknown }).errorCode
      : undefined;
  const { name, code } = error as { name?: unknown; code?: unknown };
  if (errorCode === "AuthenticationFailed" || code === "AuthenticationFailed") {
    return "auth";
  }
  if (name === "RestError" && statusCode === 403) {
    return "auth";
  }
  if (
    errorCode === "BlobAlreadyExists" ||
    code === "BlobAlreadyExists" ||
    (name === "RestError" && statusCode === 412)
  ) {
    return "exists";
  }
  if (
    name === "RestError" &&
    typeof statusCode === "number" &&
    statusCode >= 400 &&
    statusCode < 500 &&
    statusCode !== 408 &&
    statusCode !== 429
  ) {
    return "permanent";
  }
  return "transient";
}

/**
 * The reason shown in "Upload could not start: {{reason}}" (AKL-823) for a rejection from
 * the SAS fetch or `start_upload`, the two requests an upload makes before the server has
 * accepted it. Every shape those calls can reject with is recognised by `name` or by its
 * fields, as `classifyUploadError` does, so this module imports neither `tokenRefresh.ts`
 * (which pulls in Keycloak) nor the error classes themselves:
 * - `TokenRefreshError`: the session has ended, or the sign-in service did not answer.
 * - `UnauthorizedError` (the interceptor's 401): its `detail`, the server's own message.
 * - The interceptor's normalized `{ message, status, data }`: `message`, which is the
 *   server's `detail` (localised by the API from `Language-Preference`) or the
 *   interceptor's already translated "Network Error" / "Server timeout error" — unless it
 *   is the untranslated placeholder the interceptor uses when the server gave no detail.
 * - Anything else ("Failed to get SAS URL for project.", an unknown shape): a generic
 *   reason, since those messages are developer text in English.
 * @param error whatever the SAS fetch or `start_upload` rejected with
 * @returns the translated reason, never empty
 */
export function uploadStartFailureReason(error: unknown): string {
  if (typeof error === "object" && error !== null) {
    const { name, kind, detail, message, status, data } = error as {
      name?: unknown;
      kind?: unknown;
      detail?: unknown;
      message?: unknown;
      status?: unknown;
      data?: unknown;
    };
    if (name === "TokenRefreshError") {
      return kind === "sessionEnded"
        ? t("your session has ended")
        : t("the sign-in service could not be reached");
    }
    if (name === "UnauthorizedError") {
      if (typeof detail === "string" && detail.trim() !== "") {
        return detail;
      }
    } else if (
      (status !== undefined || data !== undefined) &&
      typeof message === "string" &&
      message.trim() !== "" &&
      message !== NO_DETAIL_MESSAGE
    ) {
      return message;
    }
  }
  return t("unexpected error");
}

/**
 * Resolves once the browser reports it is online — at once when it already does — or
 * when `signal` aborts (Pause, Cancel or the page going away must end the wait). The wait
 * itself is unbounded: retrying into a network the browser says is down is pointless,
 * and the bound is the attempt count once the network is back. AKL-890's `whenOnline` in
 * deploymentsApi is not reused: it is module-private and cannot be aborted.
 * @param signal ends the wait early
 */
export const waitForOnline = (signal: AbortSignal): Promise<void> => {
  if (navigator.onLine !== false || signal.aborted) {
    return Promise.resolve();
  }
  return new Promise((resolve) => {
    const done = () => {
      window.removeEventListener("online", done);
      signal.removeEventListener("abort", done);
      resolve();
    };
    window.addEventListener("online", done);
    signal.addEventListener("abort", done);
  });
};

/**
 * Resolves after `ms`, or at once when `signal` aborts.
 * @param ms how long to wait
 * @param signal ends the wait early
 */
const delay = (ms: number, signal: AbortSignal): Promise<void> => {
  if (signal.aborted) {
    return Promise.resolve();
  }
  return new Promise((resolve) => {
    const done = () => {
      clearTimeout(timer);
      signal.removeEventListener("abort", done);
      resolve();
    };
    const timer = setTimeout(done, ms);
    signal.addEventListener("abort", done);
  });
};

interface UploadManagerOptions {
  projectId: number;
  projectGuid: string;
  deploymentId: number;
  deploymentName: string;
  deploymentGuid: string;
  selectedFiles: File[]; // files to upload
  retrieveSasUrl: () => Promise<string | null>;
  onUploadStarted: () => void;
  onUploadComplete: (uploadedFileCount: number) => void;
  onUploadFailed: (uploadedFileCount: number) => void;
  resilience?: UploadResilienceOptions;
}

class AzureUploadManager implements UploadManager {
  public selectedFiles;
  public handledFileHashes: string[];
  public priorUploadFileHashes;
  public uploadProgress;
  public isPaused;
  public onFileHandled: (
    fileHash: string,
    progress: number,
    skipped: boolean,
  ) => void;
  public onFileNotUploaded: (
    fileHash: string,
    progress: number,
    notUploadedCount: number,
  ) => void;
  public onUploadStarted: () => void;
  public onUploadComplete: (uploadedFileCount: number) => void;
  public onUploadFailed: (uploadedFileCount: number) => void;
  public onUploadStopped: (notUploadedCount: number) => void;
  public onUploadPaused: () => void;
  public onRetryStatus: (status: UploadRetryStatus) => void;
  private projectId: number;
  private projectGuid: string;
  private deploymentId: number;
  private deploymentName: string;
  private deploymentGuid: string;
  private retrieveSasUrl: () => Promise<string | null>;
  private sasUrl: string | null = null;
  /** When `sasUrl` was fetched (`Date.now()`), so it can be replaced before it expires. */
  private sasIssuedAt: number | null = null;
  private uploadedFileCount: number = 0;
  private uploadStarted: boolean = false;
  /** Set by `abandonUpload()`: the page went away; see that method. */
  private abandoned: boolean = false;
  /** Set by `cancelUpload()` and `failUpload()`: the user ended the upload. */
  private cancelled: boolean = false;
  /**
   * Indices into `selectedFiles` still to be settled in this pass, in order. The head is
   * the file being worked on; it is shifted only once the file is uploaded, skipped or
   * given up, so a pause, an expired SAS or a stop leaves it for Resume to pick up.
   */
  private pending: number[];
  /** Indices given up in this pass (a permanent refusal, or every attempt used). */
  private notUploaded: number[] = [];
  /** Aborts the request in flight: the stall watchdog, Cancel and a page going away. */
  private inFlight: AbortController | null = null;
  /** Ends a wait for the network or a back-off: Pause, Cancel and a page going away. */
  private waitAbort: AbortController = new AbortController();
  /**
   * The pass currently running, so a Resume that arrives while one is still finishing its
   * in-flight file joins it rather than starting a second pass over the same queue.
   */
  private activePass: Promise<void> | null = null;
  private readonly retryDelaysMs: readonly number[];
  private readonly maxAttempts: number;
  private readonly stallTimeoutMs: number;
  private readonly stallFloorBytesPerSecond: number;

  /**
   * Creates an instance of AzureUploadManager.
   *
   * @param {UploadManagerOptions} options - The options for configuring the upload manager.
   * @param {File[]} options.selectedFiles - The files selected for upload.
   * @param {Set<string>} options.priorUploadFileHashes - The set of file hashes that were previously uploaded for this deployment.
   * @param {number} options.projectId - The ID of the project associated with the upload.
   * @param {string} options.projectGuid - The GUID of the project associated with the upload.
   * @param {number} options.deploymentId - The ID of the deployment associated with the upload.
   * @param {string} options.deploymentName - The name of the deployment associated with the upload.
   * @param {string} options.deploymentGuid - The GUID of the deployment associated with the upload.
   * @param {Function} options.onSuccess - The callback function to be called upon successful upload of a single file.
   * @param {Function} options.retrieveSasUrl - The function to retrieve the SAS URL for uploading files.
   * @param {Function} options.onUploadComplete - The callback function to be called when all files have been successfully uploaded.
   * @param {Function} options.onUploadFailed - The callback function to be called when the upload fails.
   * @param {UploadResilienceOptions} [options.resilience] - Overrides for the retry schedule, attempt count and stall budget (tests only).
   */
  constructor(options: UploadManagerOptions) {
    this.selectedFiles = options.selectedFiles;
    this.handledFileHashes = [];
    this.priorUploadFileHashes = new Set<string>();
    this.uploadProgress = 0;
    this.isPaused = true;
    this.projectId = options.projectId;
    this.projectGuid = options.projectGuid;
    this.deploymentId = options.deploymentId;
    this.deploymentName = options.deploymentName;
    this.deploymentGuid = options.deploymentGuid;
    this.sasUrl = null;
    this.onFileHandled = () => {};
    this.onFileNotUploaded = () => {};
    this.onUploadStopped = () => {};
    this.onUploadPaused = () => {};
    this.onRetryStatus = () => {};
    this.retrieveSasUrl = options.retrieveSasUrl;
    this.onUploadStarted = options.onUploadStarted;
    this.onUploadComplete = options.onUploadComplete;
    this.onUploadFailed = options.onUploadFailed;
    this.uploadedFileCount = 0;
    this.uploadStarted = false;
    this.pending = options.selectedFiles.map((_, index) => index);
    this.retryDelaysMs =
      options.resilience?.retryDelaysMs ?? UPLOAD_RETRY_DELAYS_MS;
    this.maxAttempts = options.resilience?.maxAttempts ?? UPLOAD_MAX_ATTEMPTS;
    this.stallTimeoutMs =
      options.resilience?.stallTimeoutMs ?? UPLOAD_STALL_TIMEOUT_MS;
    this.stallFloorBytesPerSecond =
      options.resilience?.stallFloorBytesPerSecond ??
      UPLOAD_STALL_FLOOR_BYTES_PER_SECOND;
  }

  getProjectId(): number {
    return this.projectId;
  }

  getProjectGuid(): string {
    return this.projectGuid;
  }

  getDeploymentId(): number {
    return this.deploymentId;
  }

  getDeploymentName(): string {
    return this.deploymentName;
  }

  getDeploymentGuid(): string {
    return this.deploymentGuid;
  }

  /**
   * Starts — or, after a pause, an expired SAS or a stop, continues — the upload of the
   * selected files to Azure Blob Storage.
   *
   * One pass: fetch the SAS URL if there is none, register the upload with the server if
   * it has not accepted it yet, then work through the pending files in order — hash each
   * one, skip non-images, hidden files and duplicates, and send the rest one at a time.
   * A file that arrives is counted only then. When every pending file is settled the
   * upload completes; if any were given up they are queued again and the upload stops
   * instead (`onUploadStopped`), so Resume retries exactly those files and Cancel records
   * the failure.
   *
   * Resilience (AKL-889): each file is retried across a network gap rather than ending
   * the batch — see `sendWithRetries` for the classifier, the wait for `online`, the
   * back-off and the stall watchdog. This method never rejects: any error the pass does
   * not handle itself — the SAS fetch, `start_upload`, hashing — puts the upload in the
   * stopped state with its file list intact rather than leaving the UI frozen on a
   * promise nobody caught. A page that has gone away (`abandonUpload`) is left alone.
   * When the rejection came before the server accepted the upload — the SAS fetch or
   * `start_upload` itself — the user is also told "Upload could not start: {{reason}}"
   * (AKL-823; see `uploadStartFailureReason`), since nothing else on screen says the upload
   * never began. A user's pause during that failure stays quiet, as below.
   *
   * The SAS (AKL-891): the server's SAS lasts 10 h. Before each file the manager checks
   * how old its SAS is and fetches a new one when it has passed `SAS_REFRESH_AGE_MS`; a
   * file the blob store refuses with `AuthenticationFailed` regardless gets a new SAS and
   * one immediate retry (`sendWithRetries`). Only when that retry is refused too does the
   * manager pause — and then it says so (`onUploadPaused`), so the box reads "Upload
   * paused" with Resume rather than "Uploading files" with a Pause button. A SAS fetch
   * that fails lands in the stopped state like any other rejection in the pass.
   *
   * Only one pass runs at a time (review cycle 6). Pause deliberately lets the request in
   * flight finish, so a Resume can arrive while the paused pass is still awaiting it; a
   * second pass would then work the same queue head — two requests for one file, one
   * `inFlight` controller overwritten so Cancel could only abort the newer request, and,
   * because each completion shifts the queue, a file dropped from the queue unsent. When
   * a pass is already running, Resume joins it instead: it has just cleared `isPaused`,
   * so that pass carries on from where it stopped.
   *
   * @returns {Promise<void>} resolves when the pass has ended, however it ended
   */
  async startUpload(): Promise<void> {
    // A fresh controller for the waits: `pauseUpload` aborted the last one, and both a
    // new pass and a resume onto a running pass may wait again.
    this.waitAbort = new AbortController();
    if (this.activePass) {
      return this.activePass;
    }
    const pass = (async () => {
      try {
        await this.runPass();
      } catch (error) {
        if (this.abandoned || this.cancelled) {
          return; // a discarded or cancelled upload has no stopped state to enter
        }
        if (this.isPaused) {
          // The user paused while the request that failed was in flight — a SAS fetch
          // during a network drop, say. Their pause stands: no stopped state over it, the
          // head of the queue stays where it is, and Resume retries the request (a failed
          // SAS fetch has already cleared `sasUrl`) (review cycle 3).
          console.warn(
            "Upload paused while a request was failing; Resume retries it:",
            error,
          );
          return;
        }
        console.error("Upload stopped: ", error);
        if (!this.uploadStarted) {
          // The server never accepted this upload: the SAS fetch or `start_upload` failed.
          // The stopped state alone read like a paused upload, so say it did not start and
          // why (AKL-823). Resume asks the server again.
          toast.error(
            t("Upload could not start: {{reason}}", {
              reason: uploadStartFailureReason(error),
            }),
          );
        }
        this.stopPass();
      }
    })();
    this.activePass = pass;
    try {
      await pass;
    } finally {
      this.activePass = null;
    }
  }

  /**
   * Whether this pass has already said "Skipping files already in akili..." (AKL-588), so a
   * folder that is mostly in storage says it once, like "Skipping duplicate files...".
   */
  private existsNoticeShown = false;

  /**
   * One pass over the pending files; see `startUpload`. Rejections propagate to it.
   */
  private async runPass(): Promise<void> {
    // Callback must be set before starting the upload
    if (!this.onFileHandled) {
      throw new Error("onFileHandled callback is not set.");
    }
    this.existsNoticeShown = false;

    if (!this.sasUrl) {
      // Before start_upload: an empty SAS rejects here, so no lock is taken for an upload
      // that cannot send anything.
      await this.refreshSasUrl();
      if (this.abandoned || this.cancelled || this.isPaused) {
        // The page went away, the user cancelled, or the user paused while the SAS was
        // being fetched: nothing has been asked of the server yet, and a paused upload
        // must not take the lock and open a History row now — Resume registers it
        // (review cycle 1).
        return;
      }
    }

    // Register the upload — once. `uploadStarted` rather than "nothing handled yet": a
    // stop on the very first file and a Resume must not open a second History row.
    if (!this.uploadStarted) {
      const temp = await startDeploymentUpload(this.deploymentId, {
        file_count: this.selectedFiles.length,
      }); // include the number of files selected for the upload
      if (this.abandoned || this.cancelled) {
        // The page went away, or the user cancelled, while the server was answering. The
        // provider's beacon (abandoned) has already recorded CANCELLED, or `cancelUpload()`
        // (cancelled) sent nothing because the server had not accepted the upload yet; if
        // this answer granted the lock regardless, release it here — no UI is left to do so,
        // and an unreleased grant would be an orphan "In Progress" row.
        if (temp.result) {
          await endDeploymentUpload(this.deploymentId, {
            status: DeploymentStatus.CANCELLED,
            file_count: 0,
          });
        }
        return; // nothing else may run on a page that has gone away
      }
      if (!temp.result) {
        toast.error(
          t(
            "Another user is uploading to this deployment. Please try again later.",
          ),
        );
        this.uploadStarted = false;
        this.pauseSelf();
      } else {
        this.uploadStarted = true;
        // Notify that upload has started successfully (History record created on backend)
        this.onUploadStarted();
      }
      if (temp.file_hashes) {
        this.priorUploadFileHashes = new Set(temp.file_hashes);
      }
    }

    let firstSkipped = false;
    while (this.pending.length > 0) {
      if (this.isPaused) {
        return; // Exit the function completely when paused
      }
      const file = this.selectedFiles[this.pending[0]];
      const fileHash = await FileHashUtils.generateFileHash(file);
      if (this.abandoned || this.cancelled) {
        return; // abandoned or cancelled while hashing: no skip notice for a discarded upload
      }
      // Ensure the file is an image
      if (!file.type.startsWith("image/")) {
        toast.info(t("Skipping non-image file: {{file}}", { file: file.name }));
        console.log("Skipping non-image file: ", file.name);
        this.settleFile(fileHash, true);
        continue; // Skip to the next iteration for non-image files
      }

      // Skip hidden files that start with dot
      if (file.name.startsWith(".")) {
        console.log(`Skipping hidden file: ${file.name}`);
        this.settleFile(fileHash, true);
        continue; // Skip to the next iteration for hidden files
      }

      // Skip files that have already been uploaded to this deployment already
      if (this.priorUploadFileHashes.has(fileHash)) {
        if (!firstSkipped) {
          firstSkipped = true;
          toast.info(t("Skipping duplicate files..."));
        }

        this.settleFile(fileHash, true);
        continue; // Skip to the next iteration for duplicate files
      }

      // Skip files that have already been handled in this upload session
      if (this.handledFileHashes.includes(fileHash)) {
        toast.info(
          t("Skipping duplicate within upload selection: {{file}}", {
            file: file.name,
          }),
        );
        console.log(
          "Skipping duplicate file within upload selection: ",
          file.name,
        );
        this.settleFile(fileHash, true);
        continue; // Skip to the next iteration for duplicate files
      }

      // A SAS that has been held for 8 h is replaced before this file rather than found
      // expired in the middle of it (AKL-891). A fetch that fails rejects out of the pass
      // into the stopped state, with `sasUrl` cleared so Resume fetches again.
      if (this.sasIsStale()) {
        await this.refreshSasUrl();
        if (this.abandoned || this.cancelled || this.isPaused) {
          return; // the head stays pending; nothing was sent
        }
      }

      // Upload the file to Azure Blob Storage
      const folderPrefix = `deployment-${this.deploymentGuid}/`;
      const fileName = getFileName(file);
      const blobName = `${folderPrefix}${fileName}`;
      const outcome = await this.sendWithRetries(blobName, file, fileHash);
      if (outcome === "stop") {
        return; // paused, cancelled, abandoned, or the SAS path: the head stays pending
      }
    }

    if (this.notUploaded.length > 0) {
      this.stopPass();
      return;
    }
    this.onUploadComplete(this.uploadedFileCount);
  }

  /**
   * Sends one file, retrying it across a network gap (AKL-889) and across an expired SAS
   * (AKL-891).
   *
   * Each attempt builds its blob client from the SAS the manager holds *now* and hands
   * `uploadData` its own `AbortController` and an `onProgress`; a stall watchdog aborts the
   * request when no progress has been reported within the file's budget (`stallBudgetMs`).
   * On rejection the error is classified (`classifyUploadError`): `permanent`, or
   * `transient` on the last attempt, gives the file up (`giveUpFile`) and the pass moves
   * on; `transient` otherwise waits for the browser to be online, backs off along the
   * schedule, and tries again. Pause, Cancel and a page going away end a wait at once; a
   * paused file restarts from the first attempt when resumed. The uploaded count is
   * incremented only after `uploadData` resolves.
   *
   * `auth` — the blob store refused the SAS — fetches a new SAS **once** and retries the
   * same file at once; that retry is not a network failure and does not count against the
   * file's `maxAttempts`. If the new SAS is refused too, the manager toasts "Upload access
   * expired. Select Resume to continue.", clears the SAS so Resume fetches another, and
   * pauses itself *visibly* (`pauseSelf` → `onUploadPaused`); the file stays at the head of
   * the queue. A SAS fetch that fails rejects out of the pass into the stopped state.
   *
   * @param blobName the blob to write, under the deployment's folder
   * @param file the file to send
   * @param fileHash its hash
   * @returns `settled` when the file was uploaded or given up (the queue has moved on),
   *          `stop` when the pass must return with the file still pending
   */
  private async sendWithRetries(
    blobName: string,
    file: File,
    fileHash: string,
  ): Promise<"settled" | "stop"> {
    // Specify data transfer options
    const uploadOptions = {
      blockSize: UPLOAD_BLOCK_SIZE_BYTES, // 1 MiB max block size
      concurrency: UPLOAD_CONCURRENCY, // maximum number of parallel transfer workers
      maxSingleShotSize: UPLOAD_BLOCK_SIZE_BYTES, // 1 MiB max size for a single blob
      blobHTTPHeaders: { blobContentType: file.type },
      // Never write over a stored photo (AKL-588): storage refuses the write when a blob
      // already has this name, on the single-shot Put Blob and on the final Put Block List
      // alike, and `classifyUploadError` reads that refusal as `exists`.
      conditions: { ifNoneMatch: "*" },
      metadata: {
        name: file.name,
        hash: fileHash,
        dep_id: this.deploymentId.toString(),
      },
    };
    const budget = this.stallBudgetMs(file.size);
    let retried = false;
    let sasRefreshed = false;
    // Counted up only on a transient failure: the SAS retry re-runs the same attempt.
    let attempt = 1;

    for (;;) {
      if (this.isPaused) {
        return "stop"; // Exit the function completely when paused
      }
      const blockBlobClient = this.blockBlobClientFor(blobName);
      const controller = new AbortController();
      this.inFlight = controller;
      let watchdog = setTimeout(() => controller.abort(), budget);
      const onProgress = () => {
        clearTimeout(watchdog);
        watchdog = setTimeout(() => controller.abort(), budget);
      };
      let failure: { error: unknown } | null = null;
      try {
        console.log(
          "Uploading file: ",
          file.name,
          " with hash ",
          fileHash,
          " to Azure Blob Storage",
          attempt > 1 ? ` (attempt ${attempt} of ${this.maxAttempts})` : "",
        );
        await blockBlobClient.uploadData(file, {
          ...uploadOptions,
          abortSignal: controller.signal,
          onProgress,
        });
      } catch (error) {
        failure = { error };
      } finally {
        clearTimeout(watchdog);
        if (this.inFlight === controller) {
          this.inFlight = null;
        }
      }

      if (!failure) {
        this.uploadedFileCount += 1; // only now: the file has arrived
        this.settleFile(fileHash, false);
        if (retried) {
          this.onRetryStatus({ kind: "none" });
        }
        return "settled";
      }
      if (this.abandoned || this.cancelled || this.isPaused) {
        // Abandoned or cancelled while sending: no error toast. Paused while sending
        // (review cycle 3): the user asked for quiet, so no SAS fetch, no toast, no
        // self-pause on top of theirs — the file stays at the head and Resume tries it again.
        return "stop";
      }
      const kind = classifyUploadError(failure.error);
      if (kind === "exists") {
        // Already in akili under this name (AKL-588): an earlier upload, Akili Transfer, or
        // this file's own earlier attempt whose answer was lost. Nothing was written.
        if (!this.existsNoticeShown) {
          this.existsNoticeShown = true;
          toast.info(t("Skipping files already in akili..."));
        }
        this.settleFile(fileHash, true);
        if (retried) {
          this.onRetryStatus({ kind: "none" });
        }
        return "settled";
      }
      if (kind === "auth") {
        if (!sasRefreshed) {
          // The SAS may simply have run out under this file (AKL-891): fetch a new one and
          // try the same file again at once — no back-off, no attempt spent.
          sasRefreshed = true;
          console.log("SAS refused; fetching a new one and retrying the file.");
          await this.refreshSasUrl();
          if (this.abandoned || this.cancelled || this.isPaused) {
            return "stop"; // the wait was ended by Pause, Cancel or the page going away
          }
          continue;
        }
        // A fresh SAS was refused too: this is not a stale SAS. Pause, and — the fix the
        // ticket is named for — tell the context, so the box says "Upload paused" with a
        // Resume button rather than "Uploading files" with a Pause button.
        toast.info(t("Upload access expired. Select Resume to continue."));
        this.sasUrl = null; // Reset the SAS URL so Resume fetches a new one
        console.log("Pausing upload: a newly fetched SAS was refused.");
        this.pauseSelf();
        return "stop";
      }
      if (kind === "permanent" || attempt >= this.maxAttempts) {
        this.giveUpFile(file, fileHash, failure.error);
        if (retried) {
          this.onRetryStatus({ kind: "none" });
        }
        return "settled";
      }
      retried = true;
      console.warn(
        `Upload of ${file.name} failed on attempt ${attempt} of ${this.maxAttempts}; retrying`,
        failure.error,
      );
      const signal = this.waitAbort.signal;
      const retryingStatus: UploadRetryStatus = {
        kind: "retrying",
        filename: file.name,
        attempt: attempt + 1,
        maxAttempts: this.maxAttempts,
      };
      await this.waitUntilOnline(signal);
      if (this.isPaused || this.abandoned) {
        return "stop"; // the wait was ended by Pause, Cancel or the page going away
      }
      this.onRetryStatus(retryingStatus);
      await delay(
        this.retryDelaysMs[
          Math.min(attempt - 1, this.retryDelaysMs.length - 1)
        ],
        signal,
      );
      if (this.isPaused || this.abandoned) {
        return "stop";
      }
      // The network can drop again during a back-off of up to 30 s. Without this second
      // wait the next attempt starts offline and fails at once — and when that is the
      // last attempt the file is given up without ever having waited for the network,
      // which is the whole promise of this loop (review cycle 4).
      if (await this.waitUntilOnline(signal)) {
        if (this.isPaused || this.abandoned) {
          return "stop";
        }
        this.onRetryStatus(retryingStatus);
      }
      attempt += 1;
    }
  }

  /**
   * Fetches a SAS URL and records when it was issued (AKL-891). The old one is cleared
   * first, so a fetch that rejects leaves the manager with no SAS and the next pass
   * fetching again rather than retrying a SAS already known to be stale.
   * @throws when the server returns no SAS URL, or the fetch itself rejects
   */
  private async refreshSasUrl(): Promise<void> {
    this.sasUrl = null;
    this.sasIssuedAt = null;
    const sasUrl = await this.retrieveSasUrl();
    if (!sasUrl) {
      throw new Error("Failed to get SAS URL for project.");
    }
    this.sasUrl = sasUrl;
    this.sasIssuedAt = Date.now();
  }

  /**
   * Whether the SAS held has passed `SAS_REFRESH_AGE_MS` and must be replaced before the
   * next file (AKL-891). A SAS with no issue time on record counts as stale.
   */
  private sasIsStale(): boolean {
    return (
      this.sasIssuedAt === null ||
      Date.now() - this.sasIssuedAt >= SAS_REFRESH_AGE_MS
    );
  }

  /**
   * A client for `blobName` under the SAS the manager holds right now — built per attempt,
   * so an attempt made after the SAS was replaced uses the new one (AKL-891).
   * @param blobName the blob to write
   */
  private blockBlobClientFor(blobName: string): BlockBlobClient {
    if (!this.sasUrl) {
      throw new Error("Failed to get SAS URL for project.");
    }
    return new ContainerClient(this.sasUrl).getBlockBlobClient(blobName);
  }

  /**
   * Waits until the browser reports a network again, saying so in the upload box while it
   * waits. Returns at once when already online.
   * @param signal ends the wait early (Pause, Cancel, the page going away)
   * @returns whether it actually had to wait, so the caller can restore its own status
   */
  private async waitUntilOnline(signal: AbortSignal): Promise<boolean> {
    if (navigator.onLine !== false) {
      return false;
    }
    this.onRetryStatus({ kind: "waitingForNetwork" });
    await waitForOnline(signal);
    return true;
  }

  /**
   * How long a request for a file of `size` bytes may go without a progress report
   * before the watchdog aborts it: the floor (60 s), or — for a file whose bytes in
   * flight could not complete in 60 s at the floor link speed — the time they need. The
   * SDK reports progress only per completed request, so a flat 60 s would abort every
   * file over about 1 MiB on a live link under roughly 85 KB/s.
   * @param size the file's size in bytes
   * @returns the budget in ms
   */
  private stallBudgetMs(size: number): number {
    const inFlight = Math.min(
      size,
      UPLOAD_BLOCK_SIZE_BYTES * UPLOAD_CONCURRENCY,
    );
    return Math.max(
      this.stallTimeoutMs,
      Math.ceil((inFlight / this.stallFloorBytesPerSecond) * 1000),
    );
  }

  /**
   * The fraction of the selection settled in this pass — uploaded, skipped or given up —
   * as a percentage, held at 99 while anything is still owed.
   */
  private progress(): number {
    const settled = this.handledFileHashes.length + this.notUploaded.length;
    let progress = Math.min(
      Math.round((settled / this.selectedFiles.length) * 100),
      100,
    );
    if (
      progress === 100 &&
      (this.pending.length > 0 || this.notUploaded.length > 0)
    ) {
      progress = 99; // Do not set to 100 percent until upload is complete
    }
    return progress;
  }

  /**
   * The file at the head of the queue was uploaded or skipped: record its hash, move on,
   * and tell the UI.
   * @param fileHash the file's hash
   * @param skipped whether the file was skipped rather than uploaded
   */
  private settleFile(fileHash: string, skipped: boolean): void {
    this.pending.shift();
    this.handledFileHashes.push(fileHash);
    this.onFileHandled(fileHash, this.progress(), skipped);
  }

  /**
   * The file at the head of the queue is given up for this pass (AKL-889): it is named in
   * a toast — the only per-file report the UI has — kept for a Resume, and the UI is told
   * it is neither uploaded nor skipped. The pass moves on to the next file.
   * @param file the file
   * @param fileHash its hash
   * @param error the last failure
   */
  private giveUpFile(file: File, fileHash: string, error: unknown): void {
    console.error(t("Failed to upload file: "), error);
    toast.error(
      t("Error uploading file: {{filename}}", { filename: file.name }),
    );
    const index = this.pending.shift();
    if (index !== undefined) {
      this.notUploaded.push(index);
    }
    this.onFileNotUploaded(fileHash, this.progress(), this.notUploaded.length);
  }

  /**
   * Ends the pass without completing (AKL-889): files given up this pass go back to the
   * head of the queue for a Resume, the manager pauses itself, and the UI is told how many
   * files were not uploaded.
   */
  private stopPass(): void {
    const notUploadedCount = this.notUploaded.length;
    this.pending = [...this.notUploaded, ...this.pending];
    this.notUploaded = [];
    this.isPaused = true;
    this.onRetryStatus({ kind: "none" });
    this.onUploadStopped(notUploadedCount);
  }

  /**
   * Pauses the upload process to stop the upload of additional files. A wait for the
   * network or a back-off ends at once; a request in flight is left to finish. This is the
   * user's pause — the provider calls it and sets its own `uploadIsPaused` alongside — and
   * the mechanics the manager's own pauses share through `pauseSelf()`, which then also
   * tells the context (AKL-891).
   *
   * The retry line is cleared here (AKL-889 review cycle 2): the provider clears it too on
   * a user pause, but a self-pause with a file already on its second attempt would
   * otherwise have left "Retrying {{filename}} ({{attempt}} of {{max}})" on screen with
   * nothing running.
   */
  pauseUpload(): void {
    this.isPaused = true;
    this.waitAbort.abort();
    this.onRetryStatus({ kind: "none" });
  }

  /**
   * A pause the manager performs on itself — a fresh SAS refused, another user holding
   * the lock — as opposed to one the user asked for (AKL-891). Does what `pauseUpload`
   * does and then tells the context, so the provider's `uploadIsPaused` follows and the
   * box reads "Upload paused" with Resume. Before this the context was never told, and the
   * box kept saying "Uploading files" with a Pause button while nothing was running.
   */
  private pauseSelf(): void {
    this.pauseUpload();
    this.onUploadPaused();
  }

  /**
   * Resumes a paused or stopped upload to continue the upload process. `startUpload`
   * never rejects; the handler is belt and braces.
   */
  resumeUpload(): void {
    this.isPaused = false;
    this.startUpload().catch(console.error);
  }

  /**
   * Ends the upload because its page is going away — tab closed, reloaded, navigated off,
   * or parked in the browser's back/forward cache (AKL-890). Nothing is sent from here:
   * the provider's keep-alive beacon tells the server CANCELLED. What this closes is every
   * way the manager could carry on if the browser restores the page: the loop stops at its
   * next check, the callbacks into the UI are detached so a file already in flight finishes
   * silently — no skip notice for a file it was hashing, no error toast for one it was
   * sending — and a `startUpload()` still awaiting the server's answer to `start_upload`
   * releases the lock that answer grants instead of keeping it. The request in flight and
   * any wait are aborted too (AKL-889), so a discarded upload holds no socket open.
   */
  abandonUpload(): void {
    this.abandoned = true;
    this.isPaused = true;
    this.waitAbort.abort();
    this.inFlight?.abort();
    this.onFileHandled = () => {};
    this.onFileNotUploaded = () => {};
    this.onUploadStarted = () => {};
    this.onUploadComplete = () => {};
    this.onUploadFailed = () => {};
    this.onUploadStopped = () => {};
    this.onUploadPaused = () => {};
    this.onRetryStatus = () => {};
  }

  /**
   * Cancels the upload process and resets the state. The request in flight is aborted
   * (AKL-889) — Cancel stops what is running rather than letting it dangle — and the
   * server is told CANCELLED with the count of files that arrived. A cancel during the
   * preparation (the SAS fetch or `start_upload` still awaited) sends nothing from here,
   * because the server has not accepted the upload; `runPass` sees `cancelled` after
   * those awaits and releases a lock the late answer grants, so no "In Progress" row is
   * left behind.
   *
   * @returns void
   */
  async cancelUpload(): Promise<void> {
    this.cancelled = true;
    this.isPaused = true;
    this.waitAbort.abort();
    this.inFlight?.abort();
    this.onFileHandled = () => {};
    this.sasUrl = null;
    if (this.uploadStarted && this.deploymentId) {
      await endDeploymentUpload(this.deploymentId, {
        status: DeploymentStatus.CANCELLED,
        file_count: this.uploadedFileCount,
      });
    }
    this.resetState();
  }

  /**
   * The final give-up from the stopped state (AKL-889): the upload ends for good and the
   * server is told FAILED with the count of files that arrived — through AKL-890's
   * retrying helper, which waits for the network, retries and sends the pending status as
   * a keep-alive beacon if the page is left meanwhile. Nothing is sent when the server
   * never accepted the upload: there is no lock to release and no row to close.
   *
   * @returns the helper's outcome, or `null` when nothing was sent
   */
  async failUpload(): Promise<EndUploadOutcome | null> {
    this.cancelled = true;
    this.isPaused = true;
    this.waitAbort.abort();
    this.inFlight?.abort();
    this.onFileHandled = () => {};
    this.sasUrl = null;
    const outcome =
      this.uploadStarted && this.deploymentId
        ? await endDeploymentUploadWithRetry(this.deploymentId, {
            status: DeploymentStatus.FAILED,
            file_count: this.uploadedFileCount,
          })
        : null;
    this.resetState();
    return outcome;
  }

  private resetState(): void {
    this.uploadedFileCount = 0;
    this.handledFileHashes = [];
    this.priorUploadFileHashes = new Set<string>();
    this.uploadProgress = 0;
    this.pending = [];
    this.notUploaded = [];
  }
}

export default AzureUploadManager;
